import { useTranslation } from 'react-i18next';

import { getRecordValue } from '../../../../helpers/safeRecordAccess';
import type { FormStatus } from '../../types/dashboard.types';

interface StatusBadgeProps {
  status: FormStatus;
  pipelineStage?: string | null;
}

const statusStyles: Record<FormStatus, string> = {
  completed: 'bg-[#D1FAE5] text-[#065F46]',
  pending: 'bg-[#FEF3C7] text-[#92400E]',
  declined: 'bg-[#FEE2E2] text-[#991B1B]',
};

// Pipeline stage styles for damage insurance stages
const pipelineStageStyles: Record<string, string> = {
  quick_quote: 'bg-blue-100 text-blue-800',
  contacted: 'bg-purple-100 text-purple-800',
  real_quote: 'bg-orange-100 text-orange-800',
  app_submitted: 'bg-cyan-100 text-cyan-800',
  closed_won: 'bg-[#D1FAE5] text-[#065F46]',
  closed_lost: 'bg-[#FEE2E2] text-[#991B1B]',
  // Financial advisory stages
  qualified: 'bg-blue-100 text-blue-800',
  discovery_call: 'bg-purple-100 text-purple-800',
  needs_analysis: 'bg-orange-100 text-orange-800',
  recommendation_presented: 'bg-cyan-100 text-cyan-800',
};

export function StatusBadge({ status, pipelineStage }: StatusBadgeProps) {
  const { t } = useTranslation();

  // If pipelineStage is present, display it instead of the action.status
  if (pipelineStage) {
    const stageStyle =
      getRecordValue(pipelineStageStyles, pipelineStage) ||
      'bg-gray-100 text-gray-800';

    return (
      <span
        className={`inline-flex items-center justify-center rounded-full px-3 py-1 text-xs font-medium ${stageStyle}`}
      >
        {t(`crm.stages.${pipelineStageToKey(pipelineStage)}`)}
      </span>
    );
  }

  // Fall back to action.status display
  return (
    <span
      className={`inline-flex items-center justify-center rounded-full px-3 py-1 text-xs font-medium ${getRecordValue(statusStyles, status)}`}
    >
      {t(`dashboard.status.${status}`)}
    </span>
  );
}

/**
 * Maps snake_case pipeline stage to camelCase translation key.
 * e.g., 'quick_quote' -> 'quickQuote'
 */
function pipelineStageToKey(stage: string): string {
  return stage.replace(/_([a-z])/g, (_, letter: string) =>
    letter.toUpperCase()
  );
}
